
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { fetchProfile, upsertProfile, createWelcomePost } from "@/services/profileService";
import { ProfileData } from "@/types/profile";
import { useProfileStorage } from "./profile/useProfileStorage";

/**
 * Hook for fetching profile data
 */
export const useProfileData = (
  setProfile: React.Dispatch<React.SetStateAction<ProfileData>>,
  setLoading: (loading: boolean) => void
) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { loadFromStorage, saveToStorage } = useProfileStorage();

  // Load from localStorage if available when component mounts
  useEffect(() => {
    try {
      const cachedProfile = loadFromStorage();
      if (cachedProfile) {
        console.log("Loading profile data from cache:", cachedProfile);
        setProfile(cachedProfile);
      }
    } catch (error) {
      console.error("Error loading profile from cache:", error);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    loadProfile();
  }, [user?.id]);

  const loadProfile = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      console.log("Loading profile for user:", user.id);
      
      // First check the cache
      const cachedProfile = loadFromStorage();
      
      if (cachedProfile) {
        console.log("Found cached profile:", cachedProfile);
        
        // If we have a complete cached profile, use it immediately
        if (cachedProfile && cachedProfile.full_name && cachedProfile.username) {
          console.log("Using complete cached profile");
          setProfile(cachedProfile);
        }
      }
      
      // Try to fetch existing profile from API
      const data = await fetchProfile(user.id);
      
      if (data) {
        console.log("Profile data loaded from API:", data);
        
        // Merge with cached data if available, prioritizing API data
        const mergedProfile = cachedProfile 
          ? { ...cachedProfile, ...data } 
          : data;
          
        setProfile(mergedProfile);
        
        // Update cache with latest data
        saveToStorage(mergedProfile);
        console.log("Updated cache with API data");
        
        // Log the profile completion status
        console.log("Is profile complete:", Boolean(mergedProfile.full_name && mergedProfile.username));
      } else {
        // Profile not found, create a new one with user data if available
        console.log("Profile not found, creating new profile");
        
        const newProfileData = {
          full_name: user.user_metadata?.full_name || cachedProfile?.full_name || "",
          username: user.user_metadata?.preferred_username || cachedProfile?.username || "",
          bio: cachedProfile?.bio || "",
          interests: cachedProfile?.interests || "",
          grade_level: cachedProfile?.grade_level || "",
          school: cachedProfile?.school || "",
          avatar_url: user.user_metadata?.avatar_url || cachedProfile?.avatar_url || "",
        };
        
        // Try to create the profile
        const newProfile = await upsertProfile(user.id, newProfileData);
        
        if (newProfile) {
          console.log("New profile created:", newProfile);
          
          // Merge with any cached data we might have
          const finalProfile = cachedProfile 
            ? { ...cachedProfile, ...newProfile }
            : newProfile;
            
          setProfile(finalProfile);
          
          // Cache the profile data in localStorage
          saveToStorage(finalProfile);
          
          // Add welcome post
          try {
            await createWelcomePost(user.id);
          } catch (welcomePostError) {
            console.error("Error creating welcome post:", welcomePostError);
          }
        }
      }
    } catch (error: any) {
      console.error("Error loading profile:", error.message);
      
      // If we have a cached profile, use it as fallback
      const cachedProfile = loadFromStorage();
      if (cachedProfile) {
        console.log("Using cached profile as fallback");
        setProfile(cachedProfile);
      }
    } finally {
      setLoading(false);
    }
  };

  return { loadProfile };
};
